import { useCallback, useState } from "react";
import type { Dock } from "@/apis/dock/dock-apis";
import type { Station } from "@/apis/station/station-apis";
import type { Vehicle } from "@/apis/vehicle/vehicle-apis";
import type { Rental } from "@/domain/rental";
import {
  useDocks,
  useRental,
  useRentalStatuses,
  useStations,
  useVehicles,
} from "@/v2/queries";

export interface UseRentalModalResult {
  isOpen: boolean;
  rental: Rental | null;
  isLoading: boolean;
  open: (rentalId: number) => void;
  close: () => void;
  vehicles: Vehicle[];
  stations: Station[];
  docks: Dock[];
  statuses: string[];
  isLoadingDropdowns: boolean;
}

/**
 * Hook for managing rental modal state and data loading.
 *
 * Handles:
 * - Modal visibility state
 * - Loading rental data via TanStack Query
 * - Loading related dropdown data (vehicles, docks, statuses, stations) in parallel
 * - Modal opens as soon as rental loads (doesn't wait for dropdown data)
 */
export function useRentalModal(): UseRentalModalResult {
  const [selectedRentalId, setSelectedRentalId] = useState<number | null>(null);

  const { data: rental, isLoading: isLoadingRental } =
    useRental(selectedRentalId);

  const hasRental = selectedRentalId != null;

  const { data: vehiclesResponse, isLoading: isLoadingVehicles } = useVehicles(
    { size: 1000, cityId: rental?.cityId },
    hasRental
  );

  const { data: stationsResponse, isLoading: isLoadingStations } = useStations(
    { size: 1000, cityId: rental?.cityId },
    hasRental
  );

  const { data: docksResponse, isLoading: isLoadingDocks } = useDocks(
    { pageSize: 1000 },
    hasRental
  );

  const { data: statuses, isLoading: isLoadingStatuses } =
    useRentalStatuses(hasRental);

  const vehicles = vehiclesResponse?.content ?? [];
  const stations = stationsResponse?.content ?? [];
  const docks = docksResponse?.content ?? [];

  // Modal is open when the loaded rental matches selectedRentalId to prevent stale cached data
  const isOpen = rental != null && rental.id === selectedRentalId;

  const isLoading = isLoadingRental;

  const isLoadingDropdowns =
    isLoadingVehicles ||
    isLoadingStations ||
    isLoadingDocks ||
    isLoadingStatuses;

  const open = useCallback((rentalId: number) => {
    setSelectedRentalId(rentalId);
  }, []);

  const close = useCallback(() => {
    setSelectedRentalId(null);
  }, []);

  return {
    isOpen,
    rental: rental || null,
    isLoading,
    open,
    close,
    vehicles,
    stations,
    docks,
    statuses: statuses ?? [],
    isLoadingDropdowns,
  };
}
